/** Sessions list — filter bar + compact cards + a detail drawer.
 *
 * Extracted from the standalone Inspections page so the same UI can be
 * scoped to ONE campaign's sessions inside its own detail page. Campaign
 * sessions and standalone sessions no longer mix in one view: a session
 * belongs to exactly one `scope` (its campaign, or "no campaign at all"),
 * so the "Campaign" filter + column that used to sit alongside every
 * other column are gone — scope is fixed for the lifetime of the
 * component, never toggled.
 *
 *   scope="standalone"          → GET /v1/sessions?campaign=none&…
 *   scope={{campaignId: "…"}}   → GET /v1/sessions?campaign=<id>&…
 *
 * Uses the enhanced `GET /v1/sessions?...&paginated=true` endpoint which
 * returns numbered offset pages:
 *   {results: Session[], count: number, page: number, page_size: number,
 *    counts: {…per-status…}}
 *
 * Supported filters (server-side): status / from-to date / free-text q
 * across notes + operator-email. State is held in component state (not
 * URL params) — a future iteration can mirror filters into the URL if a
 * "share filter" affordance shows up.
 *
 * 2026-09-17 redesign: the row used to be a wide table whose only clue
 * to "who this session is with" was an operator email — never the
 * beneficiary — and every action link jumped straight into the live
 * call room. Rows are now a compact card headlined by the beneficiary's
 * name (falling back to the session title / campaign name / a
 * "standalone session" label); clicking one opens `SessionDetailDrawer`
 * instead of navigating away, same "summary row, details behind one
 * click" shape as the beneficiary list redesign.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { DateWindowPicker, SortSelect } from "@/components/ListSortAndDate";
import {
  emptyWindow,
  windowIsSet,
  windowParams,
  type DateWindow,
} from "@/components/listParams";
import { useTranslation } from "react-i18next";

import { MuiDatePicker } from "@/components/MuiDatePicker";
import { Pagination } from "@/components/Pagination";
import { StatusPill } from "@/components/StatusPill";
import { ApiPaths, api } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/format";
import type { Session, SessionStatus } from "@/lib/types";

import { SessionDetailDrawer } from "./SessionDetailDrawer";

/** The five statuses the backend counts (subset of the full SessionStatus union). */
type CountedStatus = "created" | "open" | "recording" | "closed" | "expired";

/** Per-status counts returned by the paginated list endpoint. */
type StatusCounts = Record<CountedStatus, number> & { total: number };

type SessionsPage = {
  results: Session[];
  count: number;
  page: number;
  page_size: number;
  counts?: StatusCounts;
};

export type SessionsTableScope = "standalone" | { campaignId: string };

const ALL_STATUSES: SessionStatus[] = [
  "created",
  "open",
  "recording",
  "closed",
  "expired",
];

/** The five statuses shown in the counts strip (same five as `ALL_STATUSES`
 *  but typed narrowly so they key into `StatusCounts` without a cast). */
const COUNTED_STATUSES: CountedStatus[] = [
  "created",
  "open",
  "recording",
  "closed",
  "expired",
];

/** Visual tone per counted status — mirrors the StatusPill palette. */
const STATUS_TONE: Record<CountedStatus, string> = {
  created: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  open: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  recording: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  closed: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  expired: "border-nb-border bg-nb-surface/50 text-nb-text-3",
};

const ACTIVE_TONE = "border-nb-mint/50 bg-nb-mint/10 text-nb-mint";
const IDLE_TONE =
  "border-nb-border bg-nb-surface text-nb-text-2 hover:text-nb-text hover:border-nb-border-hover";

/** Compact pill button for the status-counts strip. */
function StatusCountPill({
  label,
  count,
  active,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 " +
        "text-xs font-medium transition-colors " +
        (active ? ACTIVE_TONE : (tone ?? IDLE_TONE))
      }
    >
      {label}
      <span className="font-mono tabular-nums">{count}</span>
    </button>
  );
}

export function SessionsTable({ scope }: { scope: SessionsTableScope }) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  // Numbered offset pagination (same prev/next control as the beneficiaries
  // + campaigns lists).
  // Sorting + "ended on <day>" (2026-09-29). The existing from/to
  // above stay on created_at and keep their exact behaviour; these are
  // the new local-day windows the backend resolves in the CAMPAIGN's
  // timezone, so "today" is the operator's day, not the browser's.
  const [sort, setSort] = useState("-created");
  const [endedWindow, setEndedWindow] = useState<DateWindow>(emptyWindow);
  const [page, setPage] = useState(1);
  const [detailSessionId, setDetailSessionId] = useState<string | null>(null);
  const PAGE_SIZE = 20;

  const scopeParam = scope === "standalone" ? "none" : scope.campaignId;

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("paginated", "true");
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    params.set("campaign", scopeParam);
    if (statusFilter) params.set("status", statusFilter);
    if (fromDate) params.set("from", `${fromDate}T00:00:00Z`);
    if (toDate) params.set("to", `${toDate}T23:59:59Z`);
    if (searchText) params.set("q", searchText);
    if (sort) params.set("sort", sort);
    for (const [k, v] of Object.entries(windowParams("ended", endedWindow))) {
      params.set(k, v);
    }
    return params.toString();
  }, [
    scopeParam,
    statusFilter,
    fromDate,
    toDate,
    searchText,
    sort,
    endedWindow,
    page,
  ]);

  const q = useQuery({
    queryKey: ["sessions-list", queryString],
    queryFn: async () => {
      const res = await api.get<SessionsPage>(
        `${ApiPaths.sessions}?${queryString}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  const hasActiveFilters =
    statusFilter ||
    fromDate ||
    toDate ||
    searchText ||
    windowIsSet(endedWindow) ||
    sort !== "-created";

  const clearFilters = () => {
    setStatusFilter("");
    setFromDate("");
    setToDate("");
    setSearchText("");
    setSort("-created");
    setEndedWindow(emptyWindow);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Status counts strip — quick-filter pills showing the breakdown
          of the full (non-status-filtered) result set. Clicking a pill
          sets or toggles the status filter. */}
      {q.data?.counts && (
        <section className="flex flex-wrap items-center gap-2">
          <StatusCountPill
            label={t("sessionsList.statsAll", "All")}
            count={q.data.counts.total}
            active={!statusFilter}
            onClick={() => {
              setStatusFilter("");
              setPage(1);
            }}
          />
          {COUNTED_STATUSES.map((s) => (
            <StatusCountPill
              key={s}
              label={t(`dashboard.sessionStatus.${s}`)}
              count={q.data!.counts![s]}
              active={statusFilter === s}
              tone={STATUS_TONE[s]}
              onClick={() => {
                setStatusFilter(statusFilter === s ? "" : s);
                setPage(1);
              }}
            />
          ))}
        </section>
      )}

      {/* Filter bar — no Campaign selector: `scope` fixes that for the
          lifetime of this component, it's never a per-row toggle. */}
      <section className="card">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-nb-text-3">
              {t("sessionsList.filterStatus")}
            </span>
            <select
              className="input mt-1.5 w-full"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t("sessionsList.filterStatusAll")}</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`dashboard.sessionStatus.${s}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-nb-text-3">
              {t("sessionsList.filterFrom")}
            </span>
            <div className="mt-1.5">
              <MuiDatePicker
                value={fromDate}
                onChange={(v) => {
                  setFromDate(v);
                  setPage(1);
                }}
                ariaLabel={t("sessionsList.filterFrom")}
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-nb-text-3">
              {t("sessionsList.filterTo")}
            </span>
            <div className="mt-1.5">
              <MuiDatePicker
                value={toDate}
                minDate={fromDate || undefined}
                onChange={(v) => {
                  setToDate(v);
                  setPage(1);
                }}
                ariaLabel={t("sessionsList.filterTo")}
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-nb-text-3">
              {t("sessionsList.filterSearch")}
            </span>
            <input
              type="search"
              className="input mt-1.5 w-full"
              placeholder={t("sessionsList.filterSearchPlaceholder")}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="block">
            <SortSelect
              value={sort}
              onChange={(v) => {
                setSort(v);
                setPage(1);
              }}
              options={["created", "ended", "started", "scheduled"]}
            />
          </label>
          <label className="block">
            <DateWindowPicker
              label={t("lists.sort.sessionEnded")}
              value={endedWindow}
              onChange={(v) => {
                setEndedWindow(v);
                setPage(1);
              }}
            />
          </label>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              className="text-xs text-nb-text-2 hover:text-nb-text"
              onClick={clearFilters}
            >
              {t("sessionsList.filterClear")}
            </button>
          </div>
        )}
      </section>

      {/* Results — compact cards, not a wide table. Clicking one opens
          the detail drawer instead of navigating straight into a page. */}
      <section>
        {q.isLoading && (
          <p className="card px-4 py-8 text-center text-sm text-nb-text-2">
            {t("dashboard.loading")}
          </p>
        )}
        {q.data && q.data.results.length === 0 && (
          <p className="card px-4 py-8 text-center text-sm text-nb-text-2">
            {t("sessionsList.noResults")}
          </p>
        )}
        {q.data && q.data.results.length > 0 && (
          <ul className="space-y-2">
            {q.data.results.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onOpen={() => setDetailSessionId(s.id)}
              />
            ))}
          </ul>
        )}
      </section>

      {q.data && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          count={q.data.count}
          onPage={setPage}
          disabled={q.isFetching}
        />
      )}

      {detailSessionId && (
        <SessionDetailDrawer
          key={detailSessionId}
          sessionId={detailSessionId}
          onClose={() => setDetailSessionId(null)}
        />
      )}
    </div>
  );
}

/** A session card's headline is WHO it's with — the beneficiary, not a
 *  status/duration/operator triple that used to be the only clue. Click
 *  anywhere on the card to open `SessionDetailDrawer`; no per-row action
 *  links here any more (they moved into the drawer). */
function SessionCard({
  session,
  onOpen,
}: {
  session: Session;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const heading =
    session.beneficiary_name ||
    session.title ||
    session.campaign_name ||
    t("sessionsList.standaloneTitle", "Standalone session");
  const initial = (heading[0] || "?").toUpperCase();

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex cursor-pointer items-center gap-4 rounded-lg border border-nb-border bg-nb-bg/60 px-4 py-3 transition-colors hover:border-nb-mint/40 hover:bg-nb-surface/40 focus:outline-none focus:ring-1 focus:ring-nb-mint"
      data-testid={`session-card-${session.id}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-nb-mint/10 text-sm font-semibold text-nb-mint">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-nb-text">
            {heading}
          </span>
          <StatusPill status={session.status} />
          {session.has_recording && (
            <span className="rounded bg-rose-500/15 px-1.5 py-0.5 font-mono text-[10px] text-rose-300">
              {t("sessionsList.recordingBadge")}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-nb-text-3">
          {[
            session.beneficiary_name && session.campaign_name
              ? session.campaign_name
              : null,
            session.operator_email,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="hidden shrink-0 items-center gap-3 text-xs text-nb-text-3 sm:flex">
        <span>{formatDateTime(session.scheduled_for || session.started_at || session.created_at)}</span>
        {session.duration_seconds != null && (
          <span className="font-mono">{formatDuration(session.duration_seconds)}</span>
        )}
      </div>
      <svg
        className="hidden h-4 w-4 shrink-0 text-nb-text-3 transition-transform group-hover:translate-x-0.5 sm:block"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 4.5l6 5.5-6 5.5"
        />
      </svg>
    </li>
  );
}
